import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';

import type { JobImprovedType } from '@/entities/job/models/job.dto';

import { useAutoProcessAtsVacancy, useJobImprovedListQuery } from '../api';
import type { JobSummaryViewModel } from './job-summary.view-model';

const ATS_RAW: JobImprovedType = 'ATS_RAW';
const defaultJobTypes: JobImprovedType[] = ['ATS', 'ATS_RAW', 'CREATED', 'CAMPAIGN'];

export type UseJobImprovedSelectionParams = {
  enabled?: boolean;
  type?: JobImprovedType[];
  onAfterAutoProcessAts?: (job: JobSummaryViewModel) => void;
};

export type UseJobImprovedSelectionResult = {
  search: string;
  selectedJob: JobSummaryViewModel | null;
  jobList: JobSummaryViewModel[];
  isJobListLoading: boolean;
  isImproving: boolean;
  onSearchChange: (value: string) => void;
  onSelectJob: (job: JobSummaryViewModel | null) => void;
};

export const useJobImprovedSelection = (params: UseJobImprovedSelectionParams = {}): UseJobImprovedSelectionResult => {
  const { enabled = true, type = defaultJobTypes, onAfterAutoProcessAts } = params;

  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [selectedJob, setSelectedJob] = useState<JobSummaryViewModel | null>(null);

  useDebounce(
    () => {
      setSearchDebounced(search);
    },
    230,
    [search],
  );

  useEffect(() => {
    if (!enabled) {
      setSelectedJob(null);
      setSearch('');
      setSearchDebounced('');
    }
  }, [enabled]);

  const { data: jobList = [], isLoading: isJobListLoading } = useJobImprovedListQuery({
    search: searchDebounced,
    type,
    enabled,
  });

  const { mutateAsync: autoProcessAtsVacancy, isLoading: isImproving } = useAutoProcessAtsVacancy();

  const inFlightRawAtsIdRef = useRef<string | null>(null);

  const handleSelectJob = useCallback(
    (job: JobSummaryViewModel | null) => {
      if (job === null) {
        inFlightRawAtsIdRef.current = null;
        setSelectedJob(null);
        return;
      }

      setSelectedJob(job);

      if (job.type !== ATS_RAW) {
        inFlightRawAtsIdRef.current = null;
        return;
      }

      if (inFlightRawAtsIdRef.current === job.jobAtsId) {
        return;
      }

      inFlightRawAtsIdRef.current = job.jobAtsId;

      autoProcessAtsVacancy({ job_ats_id: job.jobAtsId, type: ATS_RAW })
        .then((processed) => {
          inFlightRawAtsIdRef.current = null;
          setSelectedJob(processed);
          onAfterAutoProcessAts?.(processed);
        })
        .catch(() => {
          inFlightRawAtsIdRef.current = null;
          setSelectedJob(job);
        });
    },
    [autoProcessAtsVacancy, onAfterAutoProcessAts],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  return {
    search,
    selectedJob,
    jobList,
    isJobListLoading,
    isImproving,
    onSearchChange: handleSearchChange,
    onSelectJob: handleSelectJob,
  };
};
